import { jsx } from "panel";
import { selx } from "clsx";
import { SvgIconEl } from "common/components/svg-icon";
import { ExperimentalTooltipEl } from "common/components/tooltip";
import { ContextPanelMenuEl } from "common/widgets/context-panel/context-panel-menu";
import { ButtonEl } from "common/components/button";
import { DropMenuEl, ExperimentalDropMenuEl } from "common/components/drop-menu";
import { Directions, PositioningStrategy } from "common/components/drop-menu/types";
import { InputEl } from "common/components/input";
import { ItemsMenuEl } from "common/components/items-menu";
import { Placement } from "common/components/tooltip/types";
import { ButtonSize, ButtonTheme, ButtonType } from "common/components/button/constants";
import { MarkdownEl } from "../markdown";
import { ButtonBarEl } from "../button-bar";
import { SelectTheme } from "./constants";
import styles from "./index.module.styl";
const Label = ({
  includeLeftIcon,
  includeRightIcon,
  hasSelectedLabel,
  isOverride,
  selectLabel,
  selectLabelTitle,
  leftIcon,
  leftIconColor,
  rightIcon,
  rightIconColor,
  hasSlot,
  shortcutLabel
}) => {
  const hasButtonSuffixSlot = hasSlot;
  const isCaretIcon = (icon) => icon.startsWith(`caret-`);
  return /* @__PURE__ */ jsx("div", { sel: selx(styles.selectLabelWrapper), attrs: {
    title: selectLabelTitle
  }, "data-sentry-component": "Label", "data-sentry-source-file": "template.tsx" }, leftIcon && includeLeftIcon ? /* @__PURE__ */ jsx(SvgIconEl, { sel: selx(styles.icon, {
    [`${styles.caretIcon}`]: isCaretIcon(leftIcon)
  }), attrs: {
    elref: `leftIcon`,
    icon: leftIcon,
    color: leftIconColor
  }, class: {
    [styles.selectHasLabel]: hasSelectedLabel
  } }) : null, hasSelectedLabel && (isOverride ? /* @__PURE__ */ jsx(MarkdownEl, { sel: selx(styles.selectLabel), attrs: {
    text: selectLabel,
    elref: `label`,
    inline: true,
    truncate: true
  } }) : /* @__PURE__ */ jsx("div", { sel: selx(styles.selectLabel), attrs: {
    elref: `label`
  } }, selectLabel)), hasButtonSuffixSlot ? /* @__PURE__ */ jsx("slot", { attrs: {
    name: `button-suffix`
  } }) : null, rightIcon && includeRightIcon ? /* @__PURE__ */ jsx(SvgIconEl, { sel: selx(styles.icon, {
    [`${styles.caretIcon}`]: isCaretIcon(rightIcon)
  }), attrs: {
    elref: `rightIcon`,
    icon: rightIcon,
    color: rightIconColor
  }, class: {
    [styles.selectHasLabel]: hasSelectedLabel
  } }) : null, shortcutLabel && /* @__PURE__ */ jsx("span", { sel: selx(styles.shortcutLabel) }, shortcutLabel));
};
export function SelectTriggerButton({
  selectCmp
}) {
  const hasTriggerSlot = selectCmp.helpers.hasSlot(`trigger`);
  const {
    isOpen
  } = selectCmp.state;
  const theme = selectCmp.helpers.getTriggerButtonTheme();
  const hasSelectedLabel = selectCmp.helpers.hasSelectedLabel();
  const selectLabel = selectCmp.helpers.selectedLabel();
  const selectLabelTitle = selectCmp.helpers.getTitle();
  const {
    leftIcon,
    leftIconColor,
    rightIcon,
    rightIconColor
  } = selectCmp.helpers.getIcon();
  const hasSlot = selectCmp.helpers.hasSlot(`button-suffix`);
  return /* @__PURE__ */ jsx("div", { sel: selx(styles.selectTrigger), attrs: {
    elref: `trigger`
  }, class: {
    [styles.hasTriggerSlot]: hasTriggerSlot
  }, on: {
    click: selectCmp.helpers.isDropMenuDisabled() ? null : () => selectCmp.setDropMenuIsOpen(!isOpen),
    mouseenter: selectCmp.helpers.handleTriggerMouseEnter,
    mouseleave: selectCmp.helpers.handleTriggerMouseLeave
  }, "data-sentry-component": "SelectTriggerButton", "data-sentry-source-file": "template.tsx" }, (() => {
    if (selectCmp.helpers.isButtonSelect()) {
      return /* @__PURE__ */ jsx(ButtonEl, { sel: selx(styles.selectTriggerButton), class: {
        truncated: !selectCmp.attr(`no-button-truncate`)
      }, attrs: {
        theme,
        type: selectCmp.attr(`theme`) === SelectTheme.WarningActions ? ButtonType.Warn : ButtonType.Info,
        size: selectCmp.attr(`size`),
        disabled: selectCmp.attr(`disabled`),
        "disable-pressed-styles": selectCmp.params.disablePressedStyles,
        "disabled-stays-opaque": selectCmp.attr(`disabled-stays-opaque`),
        readonly: selectCmp.attr(`readonly`),
        layout: selectCmp.attr(`layout`),
        "not-focusable": hasTriggerSlot,
        pressed: isOpen,
        ...leftIcon ? {
          icon: leftIcon
        } : {},
        ...leftIconColor ? {
          "icon-color": leftIconColor
        } : {},
        "no-rounded-corners": selectCmp.attr(`no-rounded-corners`),
        "experimental-positioning": selectCmp.attr(`experimental-positioning`)
      }, on: {
        focus: !hasTriggerSlot ? selectCmp.handleFocus : null,
        blur: !hasTriggerSlot ? selectCmp.handleBlur : null
      } }, !selectCmp.attr(`icon-only`) || selectCmp.attr(`show-icon-only-down-caret`) ? /* @__PURE__ */ jsx(Label, { includeLeftIcon: false, includeRightIcon: selectCmp.helpers.shouldShowRightIcon(), hasSelectedLabel, isOverride: selectCmp.attr(`label`) !== null, selectLabel, selectLabelTitle, leftIcon, leftIconColor, rightIcon, rightIconColor, shortcutLabel: selectCmp.attr(`shortcut-label`), hasSlot }) : null, selectCmp.showClearButton() ? /* @__PURE__ */ jsx(ButtonEl, { sel: selx(styles.clearButton), attrs: {
        elref: `clearButton`,
        theme: ButtonTheme.Lightweight,
        type: ButtonType.Destructive,
        size: ButtonSize.SmallIcon,
        icon: `circle-clear`
      }, on: {
        click: selectCmp.helpers.handleClearClick
      } }) : null);
    } else if (hasTriggerSlot) {
      return /* @__PURE__ */ jsx("slot", { sel: selx(styles.triggerSlot), attrs: {
        name: `trigger`
      } });
    }
    return /* @__PURE__ */ jsx(Label, { includeLeftIcon: true, includeRightIcon: selectCmp.helpers.shouldShowRightIcon(), hasSelectedLabel, isOverride: selectCmp.attr(`label`) !== null, selectLabel, selectLabelTitle, leftIcon, leftIconColor, rightIcon, rightIconColor, shortcutLabel: selectCmp.attr(`shortcut-label`), hasSlot });
  })());
}
const DropdownBody = ({
  selectCmp
}) => {
  const {
    searchFilter,
    sectionsFromInputFn,
    dropdownBodyRenderer
  } = selectCmp.state;
  const hasBodySlot = selectCmp.helpers.hasSlot(`body`);
  const isSearchable = selectCmp.helpers.isSearchable();
  const menuWidthStyle = selectCmp.helpers.getDropMenuWidthStyle();
  const menuHeightStyle = selectCmp.helpers.getDropMenuHeightStyle();
  const menuPaddingStyle = selectCmp.helpers.getDropMenuPaddingStyle();
  if (dropdownBodyRenderer) {
    return dropdownBodyRenderer();
  }
  if (hasBodySlot) {
    return /* @__PURE__ */ jsx("slot", { attrs: {
      name: `body`
    } });
  }
  return /* @__PURE__ */ jsx(null, null, selectCmp.attr(`header-title`) ? /* @__PURE__ */ jsx("div", { attrs: {
    elref: `headerTitle`
  }, sel: selx(styles.headerTitle), style: menuWidthStyle }, selectCmp.attr(`back-button`) ? /* @__PURE__ */ jsx(ButtonEl, { sel: selx(styles.backButton), attrs: {
    theme: ButtonTheme.Tertiary,
    icon: `arrow-left`,
    title: `Back (\u21E6 Left Arrow)`
  }, on: {
    click: selectCmp.helpers.handleBackClick
  } }) : null, /* @__PURE__ */ jsx("div", { sel: selx(styles.titleText) }, selectCmp.attr(`header-title`))) : null, isSearchable ? /* @__PURE__ */ jsx(InputEl, { attrs: {
    elref: `searchInput`
  }, key: selectCmp.attr(`header-title`), sel: selx(styles.searchInput), style: menuWidthStyle, params: {
    hideFocusStyling: selectCmp.attr(`hide-input-focus-styling`),
    icon: `search`,
    placeholder: selectCmp.attr(`search-placeholder`),
    showClear: true,
    onClear: () => selectCmp.helpers.handleSearchInput(``),
    onInput: (v) => selectCmp.helpers.handleSearchInput(v)
  }, props: {
    value: searchFilter
  }, hook: {
    insert: selectCmp.helpers.handleSearchInputInsert,
    destroy: () => selectCmp.focus()
  } }) : null, /* @__PURE__ */ jsx(ItemsMenuEl, { key: selectCmp.attr(`header-title`), style: {
    ...menuWidthStyle,
    ...menuHeightStyle,
    ...menuPaddingStyle
  }, attrs: {
    elref: `selectItemsMenu`,
    tabindex: 0,
    multiple: selectCmp.attr(`multiple`),
    "show-select-all": selectCmp.attr(`show-select-all`),
    "search-filter": searchFilter,
    "empty-message": selectCmp.attr(`empty-message`),
    "align-all-text": selectCmp.attr(`align-all-text`),
    "disable-first-item-padding": selectCmp.attr(`disable-first-item-padding`),
    theme: selectCmp.attr(`list-theme`),
    "show-section-label-matches": selectCmp.attr(`show-section-label-matches`)
  }, props: {
    sections: selectCmp.helpers.getItemsMenuSections(),
    sectionsFromInputFn,
    keydownProxyEl: selectCmp,
    contextPanelTransform: selectCmp.helpers.itemContextPanelTransform(),
    contextPanelPositionParentElement: selectCmp.contextPanelPositionParentElement
  }, on: {
    clickItem: selectCmp.helpers.handleClickItem,
    focusItem: selectCmp.helpers.handleFocusItem,
    selectAll: selectCmp.helpers.handleSelectAll
  }, "data-sentry-element": "ItemsMenuEl", "data-sentry-source-file": "template.tsx" }, selectCmp.helpers.hasSlot(`context-panel-content`) && /* @__PURE__ */ jsx("slot", { attrs: {
    name: `context-panel-content`,
    slot: `context-panel-content`
  } })), selectCmp.attr(`multiple-save-button`) ? /* @__PURE__ */ jsx(ButtonBarEl, { on: {
    click: selectCmp.helpers.handleSaveClicked
  } }, "Save") : null);
};
export function Template() {
  const {
    isOpen,
    tooltipData
  } = this.state;
  const hasTriggerSlot = this.helpers.hasSlot(`trigger`);
  const hasFooterSlot = this.helpers.hasSlot(`footer`);
  const tooltip = this.helpers.getTooltip();
  const DropdownEl = this.attr(`experimental-positioning`) ? ExperimentalDropMenuEl : DropMenuEl;
  return /* @__PURE__ */ jsx("div", { sel: selx(styles.selectContainer), class: {
    [styles.hasTriggerSlot]: hasTriggerSlot
  }, "data-sentry-component": "Template", "data-sentry-source-file": "template.tsx" }, !isOpen && tooltip && /* @__PURE__ */ jsx(ExperimentalTooltipEl, { attrs: {
    placement: Placement.Top,
    "delay-in": 10,
    "delay-out": 10,
    ...tooltip
  }, params: {
    text: tooltip.text
  }, props: {
    parentEl: this
  } }), !isOpen && this.helpers.hasHoverContextPanel() && tooltipData ? /* @__PURE__ */ jsx(ContextPanelMenuEl, { attrs: {
    elref: `hoverContextPanel`,
    placement: tooltipData.placement || Directions.Down,
    "position-strategy": PositioningStrategy.Fixed
  }, params: {
    data: tooltipData
  }, on: {
    contextPanelHoverIn: this.helpers.handleContextPanelHoverIn,
    contextPanelHoverOut: this.helpers.handleContextPanelHoverOut
  } }) : null, /* @__PURE__ */ jsx(SelectTriggerButton, { selectCmp: this, "data-sentry-element": "SelectTriggerButton", "data-sentry-source-file": "template.tsx" }), isOpen ? (() => {
    var _a;
    return /* @__PURE__ */ jsx("div", { sel: selx(styles.selectDropMenu), attrs: {
      elref: `selectDropMenuContainer`
    } }, /* @__PURE__ */ jsx(DropdownEl, { attrs: {
      "data-force-visibility": this.attr(`force-visibility`),
      elRef: `selectDropMenu`,
      open: isOpen,
      direction: this.attr(`menu-alignment`),
      "experimental-positioning": this.attr(`experimental-positioning`),
      "prevent-close": this.attr(`prevent-close`),
      "prevent-close-selectors": [
        `.create-custom-button`,
        `mp-tooltip`
        // prevent clicks on tooltips from closing the menu, allows users to click links within tooltips
      ],
      ...this.attr(`offset`) ? {
        offset: this.attr(`offset`)
      } : {}
    }, on: {
      change: (ev) => this.helpers.handleDropMenuChange(ev),
      mouseenter: this.helpers.handleMenuMouseEnter,
      mouseleave: this.helpers.handleMenuMouseLeave
    }, hook: {
      insert: this.helpers.handleDropMenuInsert
    }, props: {
      positionParent: (_a = this.dropdownPositionParent) != null ? _a : this
    } }, /* @__PURE__ */ jsx(DropdownBody, { selectCmp: this }), hasFooterSlot ? /* @__PURE__ */ jsx("div", { sel: selx(`select-footer`) }, /* @__PURE__ */ jsx("slot", { attrs: {
      name: `footer`
    } })) : null));
  })() : null);
}
